import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import PlatformsApi from '../services/platformsApi'

const PlatformsContext = createContext()

export const usePlatforms = () => {
  const context = useContext(PlatformsContext)
  if (!context) {
    throw new Error('usePlatforms must be used within a PlatformsProvider')
  }
  return context
}

export const PlatformsProvider = ({ children }) => {
  const [platforms, setPlatforms] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchPlatforms = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const platformsData = await PlatformsApi.fetchPlatforms()
      setPlatforms(platformsData)
    } catch (err) {
      setError(err.message || 'Failed to fetch platforms')
      console.error('Error in PlatformsContext fetching platforms:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPlatforms()
  }, [fetchPlatforms])

  const getActivePlatforms = useCallback(() => {
    return PlatformsApi.getActivePlatforms(platforms)
  }, [platforms])

  const normalizePlatformName = useCallback((platformName) => {
    return PlatformsApi.normalizePlatformName(platformName, platforms)
  }, [platforms])

  const getPlatformById = useCallback((platformId) => {
    return PlatformsApi.getPlatformById(platformId, platforms)
  }, [platforms])

  const getPlatformIcon = useCallback((platformId) => {
    return PlatformsApi.getPlatformIcon(platformId, platforms)
  }, [platforms])

  const getPlatformDisplayName = useCallback((platformId) => {
    return PlatformsApi.getPlatformDisplayName(platformId, platforms)
  }, [platforms])

  const value = {
    platforms,
    loading,
    error,
    refetch: fetchPlatforms,
    getActivePlatforms,
    normalizePlatformName,
    getPlatformById,
    getPlatformIcon,
    getPlatformDisplayName
  }

  return (
    <PlatformsContext.Provider value={value}>
      {children}
    </PlatformsContext.Provider>
  )
}

export default PlatformsContext
